import { createSelector } from 'reselect';
import get from 'lodash/get';
import {
  initialize,
  isVisible,
  ITERATOR_PRE_BEGIN,
  ITERATOR_POST_END,
  JUMP_TYPES,
} from './AdvWorkflowHelpers';
import { isConsumerArea, selectFeatureFlags } from '../../CommonSelectors/CommonAreaSelector';
import {
  consumerAccessWorkflowSelector,
  hadAllWorkflowsInOnboardingSelector,
} from '../../ConsumerArea/ConsumerAreaSelector';
import { CONSUMER_AREA_ACCESS_WORKFLOWS } from '../../CommonConstants';
import { taxLocations } from '../../CommonUtils/taxLocations';

export const clientId = (state, props) => props.clientId;
export const workflowId = (state, props) => props.workflowId;
export const backendKey = (state, props) => props.backendKey;
export const branch = (state, props) => props.branch;
export const advWorkflow = (state, props) => state.AdvWorkflow[workflowId(state, props)] || {};
export const currentClientId = (state, props) => advWorkflow(state, props).currentClientId;

export const selectedGoalIdSelector = state => state.AdvWorkflow.selectedGoalId;

export const currentClientIdOrDefaultSelector = createSelector(
  state => state,
  workflowId,
  (state, wkID) => get(state, `AdvWorkflow[${wkID}].currentClientId`, null),
);

export const clientWorkflowStateOrDefaultSelector = createSelector(
  state => state,
  workflowId,
  currentClientIdOrDefaultSelector,
  (state, wrkflId, clntId) => get(state, `AdvWorkflow[${wrkflId}][${clntId}]`, {}),
);

export const supportDataOrEmptyObjectSelector = createSelector(
  clientWorkflowStateOrDefaultSelector,
  clientWorkflowState => get(clientWorkflowState, 'data2', {}),
);

export const previousSupportDataOrEmptyObjectSelector = createSelector(
  clientWorkflowStateOrDefaultSelector,
  clientWorkflowState => get(clientWorkflowState, 'previousData2', {}),
);

export const graphDataOrEmptyObjectSelector = createSelector(
  clientWorkflowStateOrDefaultSelector,
  clientWorkflowState => get(clientWorkflowState, 'data', {}),
);

export const previousGraphDataOrEmptyObjectSelector = createSelector(
  clientWorkflowStateOrDefaultSelector,
  clientWorkflowState => get(clientWorkflowState, 'backupData', {}),
);

export const workflowSubdivision = (state, props) =>
  advWorkflow(state, props)[advWorkflow(state, props).currentClientId] || {};
export const tree = (state, props) => workflowSubdivision(state, props).tree;

export const summaryIteratorSelector = (state, props) => {
  const { summaryStack } = workflowSubdivision(state, props);

  if (typeof summaryStack === 'undefined') {
    return undefined;
  }

  if (summaryStack.length) {
    return summaryStack[summaryStack.length - 1];
  }
  return ITERATOR_POST_END;
};

export const data = (state, props) => fragment => {
  const parts = fragment.split('.');
  let object = workflowSubdivision(state, props).data;

  for (let i = 0; i < parts.length - 1; i += 1) {
    object = object[parts[i]] || {};
  }
  return object[parts[parts.length - 1]];
};
export const data2 = (state, props) => fragment => {
  const parts = fragment.split('.');
  let object = workflowSubdivision(state, props).data2;

  for (let i = 0; i < parts.length - 1; i += 1) {
    object = object[parts[i]] || {};
  }
  return object[parts[parts.length - 1]];
};

export const workflowIteratorSelector = createSelector(
  workflowSubdivision,
  ({ iterator }) => iterator,
);

export const isFirstStep = (state, props) =>
  isVisible(initialize(1, tree(state, props)), workflowIteratorSelector(state, props));
export const isLastStep = (state, props) =>
  isVisible(initialize(-1, tree(state, props)), workflowIteratorSelector(state, props));
export const errorMessages = (state, props) => {
  const parts = props.validationId.split('.');
  let object = workflowSubdivision(state, props).validation;

  for (let i = 0; i < parts.length - 1; i += 1) {
    object = object[parts[i]] || {};
  }
  return object[parts[parts.length - 1]] || [];
};

export const isAnyStepVisibleSelector = createSelector(
  workflowIteratorSelector,
  it => it !== ITERATOR_PRE_BEGIN && it !== ITERATOR_POST_END,
);

export const alphaSelector = createSelector(workflowSubdivision, workflow => workflow.alpha || 0.0);

export const isBusySelector = createSelector(
  workflowSubdivision,
  workflow => !!(workflow.spinner || 0),
);

export const isPopulatedSelector = createSelector(
  workflowSubdivision,
  workflow => workflow.populated || false,
);

export const isGoingForwardSelector = createSelector(
  workflowSubdivision,
  ({ jumpType }) => jumpType === JUMP_TYPES.FORWARD,
);

export const isGoingBackwardSelector = createSelector(
  workflowSubdivision,
  ({ jumpType }) => jumpType === JUMP_TYPES.BACKWARD,
);

export const jumpTypeSelector = createSelector(workflowSubdivision, ({ jumpType }) => jumpType);

export const ALL_WORKFLOWS = 6;
export const NO_WORKFLOW = -1;
export const DEFAULT_WORKFLOW = 0;

export const hasTakeMeToPlanSelector = () =>
  window.ADV.util.env('isConsumerArea') &&
  get(window, 'ADV.data.user.ConsumerAccessWorkflow', DEFAULT_WORKFLOW) === NO_WORKFLOW &&
  window.ADV.entities.isOptedIn('TakeToFinancialPlan');

export const hasTakeMeToDashboardSelector = createSelector(
  consumerAccessWorkflowSelector,
  consumerAccessWorkflow => consumerAccessWorkflow === CONSUMER_AREA_ACCESS_WORKFLOWS.NONE,
);

export const hasAddAnotherGoalSelector = createSelector(
  consumerAccessWorkflowSelector,
  hadAllWorkflowsInOnboardingSelector,
  (consumerAccessWorkflow, hadAllWorkflowsInOnboarding) =>
    consumerAccessWorkflow === CONSUMER_AREA_ACCESS_WORKFLOWS.ALL_WORKFLOWS ||
    hadAllWorkflowsInOnboarding,
);

export const finishButtonTextSelector = () =>
  window.ADV.util.env('isConsumerArea') ? 'Finish' : 'Add another goal';

// While being inside a workflow, we can exit the workflow by going
// back only when the following conditions are true:
//  1) We are NOT in consumer area or
//  2) We are in Full Access mode or
//  3) We are in All Workflows mode
// Otherwise there is no way to exit the workflow, so we must stay in it.
export const canGoBackSelector = () =>
  !window.ADV.util.env('isConsumerArea') ||
  get(window, 'ADV.data.user.ConsumerAccessWorkflow', DEFAULT_WORKFLOW) === NO_WORKFLOW ||
  get(window, 'ADV.data.user.ConsumerAccessWorkflow', DEFAULT_WORKFLOW) === ALL_WORKFLOWS;

export const iterator = createSelector(
  workflowIteratorSelector,
  summaryIteratorSelector,
  (workflowIterator, summaryIterator) => {
    if (workflowIterator === ITERATOR_POST_END) {
      return summaryIterator;
    }
    return workflowIterator;
  },
);

export const isInSummaryEditSelector = createSelector(
  workflowIteratorSelector,
  summaryIteratorSelector,
  (workflowIterator, summaryIterator) =>
    workflowIterator === ITERATOR_POST_END && summaryIterator !== ITERATOR_POST_END,
);

export const isLastViewOnDeckSelector = (state, { workflowId: wrkflID, nextIteratorsLength }) => {
  const { summaryStack } = workflowSubdivision(state, { workflowId: wrkflID });

  if (typeof summaryStack === 'undefined') {
    return false;
  }
  return summaryStack.length + nextIteratorsLength === 1;
};

export const isInSummarySelector = createSelector(iterator, it => it === ITERATOR_POST_END);
export const shouldSkipSummarySelector = createSelector(
  isConsumerArea,
  (_, { isMobile, goToSummaryWhenReady }) => isMobile && goToSummaryWhenReady !== '1',
  (isConsumerAreaArg, shouldSkip) => isConsumerAreaArg && shouldSkip,
);

export const shouldSkipSummaryMultipleGoalsSelector = createSelector(
  isConsumerArea,
  (_, { isMobile }) => isMobile,
  (isConsumerAreaArg, shouldSkip) => isConsumerAreaArg && shouldSkip,
);

export const stepsToSkipSelector = createSelector(
  graphDataOrEmptyObjectSelector,
  ({ StepsToSkip }) => {
    const stepsToSkipMap = {};
    (StepsToSkip || []).forEach(step => {
      stepsToSkipMap[step] = true;
    });
    return stepsToSkipMap;
  },
);

export const taxLocationsSelector = createSelector(selectFeatureFlags, featureFlags => {
  const featureFlag = featureFlags.find(flag => flag.name === 'CanadianProvinces');

  if (featureFlag && featureFlag.enabled === false) {
    return taxLocations.filter(location => !location.isCanada);
  }

  return taxLocations;
});
